import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin, Loader2, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Kicker } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import usePageMeta from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  usePageMeta({ title: `${t.nav.contact} | ${t.company.short}` });
  const f = t.contact.fields;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", inquiry_type: "general", message: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await axios.post(`${API}/contact`, fd);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", inquiry_type: "general", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#F5F2EB] pt-32 md:pt-40">
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Info side */}
          <div className="lg:col-span-5">
            <Kicker>{t.contact.kicker}</Kicker>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tight text-[#1A1A1A] mt-5 mb-6">
              {t.contact.title}
            </h1>
            <p className="text-lg text-[#4A4A4A] max-w-md mb-12">{t.contact.text}</p>

            <div className="space-y-8">
              <div>
                <h4 className="text-xs uppercase tracking-[0.2em] text-[#C4A27A] mb-3">
                  {t.contact.addressLabel}
                </h4>
                <p className="flex items-start gap-3 text-[#1A1A1A]">
                  <MapPin className="w-5 h-5 text-[#6A1E27] mt-0.5 shrink-0" />
                  {t.contact.address}
                </p>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-[0.2em] text-[#C4A27A] mb-3">
                  {t.contact.infoTitle}
                </h4>
                <div className="space-y-3">
                  <a href="mailto:info@ezdhar-ksa.com" className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#6A1E27] transition-colors group">
                    <Mail className="w-5 h-5 text-[#6A1E27]" /> info@ezdhar-ksa.com
                    <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                  <a href="mailto:jobs.hr@ezdhar-ksa.com" className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#6A1E27] transition-colors group">
                    <Mail className="w-5 h-5 text-[#6A1E27]" /> jobs.hr@ezdhar-ksa.com
                    <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Form side */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#E5DFD3] p-8 md:p-12">
              <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm text-[#4A4A4A] mb-2 block">{f.name}</Label>
                    <Input data-testid="contact-name" name="name" required value={form.name} onChange={onChange} />
                  </div>
                  <div>
                    <Label className="text-sm text-[#4A4A4A] mb-2 block">{f.email}</Label>
                    <Input data-testid="contact-email" type="email" name="email" required value={form.email} onChange={onChange} />
                  </div>
                  <div>
                    <Label className="text-sm text-[#4A4A4A] mb-2 block">{f.phone}</Label>
                    <Input data-testid="contact-phone" name="phone" value={form.phone} onChange={onChange} />
                  </div>
                  <div>
                    <Label className="text-sm text-[#4A4A4A] mb-2 block">{f.inquiry}</Label>
                    <Select value={form.inquiry_type} onValueChange={(v) => setForm({ ...form, inquiry_type: v })}>
                      <SelectTrigger data-testid="contact-inquiry" className="w-full">
                        <SelectValue placeholder={f.inquiry} />
                      </SelectTrigger>
                      <SelectContent>
                        {t.contact.inquiryTypes.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} data-testid={`inquiry-${opt.value}`}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-[#4A4A4A] mb-2 block">{f.message}</Label>
                  <Textarea data-testid="contact-message" name="message" required rows={5} value={form.message} onChange={onChange} />
                </div>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#6A1E27] text-white py-4 hover:bg-[#4A151C] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {f.submit}
                </motion.button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
